import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input } from '@angular/core';
import {
  ActivationRegime,
  ActivationRegimeCase,
  ActivationRegimeDto,
  ActivationRegimeEventDto
} from '@authority-labs/api-interfaces';
import { Conclusion } from '@authority-labs/enums';
import { NzModalEditTextComponent } from '@check-list/app/shared/dialogs/edit-text-dialog/edit-text-dialog.component';
import { NotesBankDialogService } from '@check-list/app/shared/dialogs/notes-bank-dialog/notes-bank-dialog.service';
import { nameof } from '@authority-labs/helpers';
import _, { cloneDeep } from 'lodash';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ActivationRegimeService } from '../../activation-regime.service';
import { CheckBasicCaseComponent } from '../check-basic-case/check-basic-case.component';

@Component({
  selector: 'authority-labs-activation-regime',
  templateUrl: './activation-regime.component.html',
  styleUrls: ['./activation-regime.component.scss']
})
export class ActivationRegimeComponent extends CheckBasicCaseComponent {
  Conclusion = Conclusion;

  @Input() inspection: ActivationRegimeCase;
  selectedTab = 0;

  constructor(
    private notesBankDialogService: NotesBankDialogService,
    private activationRegimeService: ActivationRegimeService,
    private modal: NzModalService
  ) {
    super();
  }

  tabName(index: number) {
    return `משטר הפעלות ${index + 1}`;
  }

  addActivationRegime(): void {
    this.inspection.data.push(new ActivationRegime());
    this.selectedTab = this.inspection.data.length - 1;
    this.onChange();
  }

  duplicateActivationRegime(): void {
    const data = cloneDeep(this.inspection.data[this.selectedTab]);
    this.inspection.data.push(new ActivationRegime(data));
    this.selectedTab = this.inspection.data.length - 1;
    this.onChange();
  }

  removeActivationRegime(): void {
    this.inspection.data.splice(this.selectedTab, 1);
    this.onChange();
  }

  isPassed(event: ActivationRegimeEventDto, action: string): Conclusion {
    const eventAction = event?.actions?.find((a) => a.name === action);
    switch (eventAction?.isPassed) {
      case true:
        return Conclusion.Pass;
      case false:
        return Conclusion.Fail;
      case null:
        return Conclusion.NotApplicable;
    }
  }

  toggleConclusion(event: ActivationRegimeEventDto, action: string): void {
    const eventAction = event?.actions?.find((a) => a.name === action);
    if (!eventAction) {
      event.actions.push({
        name: action,
        isPassed: true
      });
    } else {
      switch (eventAction.isPassed) {
        case true:
          eventAction.isPassed = false;
          break;
        case false:
          eventAction.isPassed = null;
          break;
        default:
          eventAction.isPassed = true;
          break;
      }
    }

    this.onChange();
  }

  addNewEvent(events: ActivationRegimeEventDto[]): void {
    this.modal.create({
      nzClosable: false,
      nzContent: NzModalEditTextComponent,
      nzComponentParams: {
        text: ''
      },
      nzOnOk: (ref) => {
        if (ref.text?.trim()) {
          events.push({ name: ref.text.trim(), actions: [] });
          this.onChange();
        }
      }
    });
  }

  addEvents(events: ActivationRegimeEventDto[]): void {
    this.notesBankDialogService
      .showMulti(`${ActivationRegime.name}_event`)
      .afterClosed()
      .subscribe((selectedEvents) => {
        if (selectedEvents) {
          selectedEvents = Array.isArray(selectedEvents)
            ? selectedEvents
            : [selectedEvents];
          const newEvents = _.map(
            events,
            nameof<ActivationRegimeEventDto>('name')
          );
          const result = _.filter(
            selectedEvents,
            (str) =>
              !_.some(newEvents, (eventName) => _.includes(str, eventName))
          );

          result.forEach((e) => events.push({ name: e, actions: [] }));

          this.onChange();
        }
      });
  }

  addNewAction(actions: string[]): void {
    this.modal.create({
      nzClosable: false,
      nzContent: NzModalEditTextComponent,
      nzComponentParams: {
        text: ''
      },
      nzOnOk: (ref) => {
        if (ref.text?.trim()) {
          actions.push(ref.text.trim());
          this.onChange();
        }
      }
    });
  }

  addActions(section: string, actions: string[]): void {
    this.notesBankDialogService
      .showMulti(`${ActivationRegime.name}_action_${section}`)
      .afterClosed()
      .subscribe((selectedActions) => {
        if (selectedActions) {
          selectedActions = Array.isArray(selectedActions)
            ? selectedActions
            : [selectedActions];
          const result = _.filter(
            selectedActions,
            (str) => !_.some(actions, (eventName) => _.includes(str, eventName))
          );

          result.forEach((action) => actions.push(action));

          this.onChange();
        }
      });
  }

  saveActivationRegime(activationRegime: ActivationRegime): void {
    const data: ActivationRegimeDto = {
      actions: activationRegime.actions,
      events: activationRegime.events.map((e) => ({
        name: e.name,
        actions: []
      }))
    };
    this.activationRegimeService.createOne(data).subscribe();
  }

  dropEvent(
    event: CdkDragDrop<ActivationRegimeEventDto[]>,
    events: ActivationRegimeEventDto[]
  ): void {
    moveItemInArray(events, event.previousIndex, event.currentIndex);
    this.onChange();
  }

  dropAction(event: CdkDragDrop<any>): void {
    const { items, index } = event.item.data;
    const targetIndex = index + (event.currentIndex - event.previousIndex);
    if (targetIndex >= 0 && targetIndex < items.length) {
      moveItemInArray(items, index, targetIndex);
      this.onChange();
    }
  }

  deleteAction(index: number, actions: string[]): void {
    actions.splice(index, 1);

    this.onChange();
  }

  deleteEvent(index: number, events: ActivationRegimeEventDto[]): void {
    events.splice(index, 1);

    this.onChange();
  }

  editAction(index: number, actions: string[]): void {
    this.modal.create({
      nzClosable: false,
      nzContent: NzModalEditTextComponent,
      nzComponentParams: {
        text: actions[index]
      },
      nzOnOk: (ref) => {
        actions[index] = ref.text;
        this.onChange();
      }
    });
  }

  editEvent(index: number, events: ActivationRegimeEventDto[]): void {
    this.modal.create({
      nzClosable: false,
      nzContent: NzModalEditTextComponent,
      nzComponentParams: {
        text: events[index].name
      },
      nzOnOk: (ref) => {
        events[index].name = ref.text;
        this.onChange();
      }
    });
  }
}
